import { useMutation, useQuery, useQueryClient } from 'react-query';
import { baseFetch } from '../api/baseFetch';
import {
  PermissionsResponseSchema,
  PermissionsUpdateResponseSchema,
  type PermissionsResponse,
} from '../api/schemas/PermissionsResponseSchema';
import type { UserPermissions } from '../api/schemas/UserPermissionsSchema';
import { useToast } from '@raptormaps/toast';
import type { OrgUser } from '../api/types/OrgUser';

export const usePermissions = (
  orgId?: number,
  userIds?: string[],
  enabled?: boolean,
) => {
  const getUserPermissions = async () => {
    let users: UserPermissions[] = [];
    let fetchedAllUsers = false;
    let nextToken = '';

    while (!fetchedAllUsers) {
      await baseFetch(
        'auth',
        `/permissions/orgs/${orgId}/user-permissions`,
        PermissionsResponseSchema,
        {
          headers: {
            'Cache-Control': 'max-age=0',
          },
          searchParams: {
            user_ids: userIds ?? '',
            limit: '100',
            pagination_token: nextToken,
          },
        },
        // eslint-disable-next-line no-loop-func
      ).then((res: PermissionsResponse) => {
        users = [...users, ...res.user_permissions];

        if (res.meta.next_token === '') {
          fetchedAllUsers = true;
        } else {
          nextToken = res.meta.next_token;
        }
      });
    }

    return users;
  };

  return useQuery(['getUserPermissions', orgId], getUserPermissions, {
    enabled: !!orgId && !!userIds?.length && enabled,
  });
};

export const useInvalidatePermissions = () => {
  const queryClient = useQueryClient();
  return {
    invalidatePermissions: () =>
      queryClient.invalidateQueries(['getUserPermissions']),
  };
};

type Actions = {
  action: string;
  value?: { user_type: string; user_id: string; role_ids: string[] };
  path?: string;
};
type Errors = {
  code: number;
  message: string;
};
const chunkArray: (arr: Actions[], size: number) => Actions[][] = (arr, size) =>
  arr.length > size
    ? [arr.slice(0, size), ...chunkArray(arr.slice(size), size)]
    : [arr];

export const useUpdatePermissions = (users: OrgUser[], orgId: number) => {
  const toast = useToast();
  const { invalidatePermissions } = useInvalidatePermissions();

  const updateUserPermissions = async () => {
    const formattedUsers = users
      .map(user => {
        const userRoles: string[] = [];
        const userIsNotRoles: string[] = [];
        if (user.sentryPilot) {
          userRoles.push('sentry_command_pilot');
        } else {
          userIsNotRoles.push('sentry_command_pilot');
        }
        if (user.sentryViewer) {
          userRoles.push('sentry_command_viewer');
        } else {
          userIsNotRoles.push('sentry_command_viewer');
        }
        if (user.sentryAdmin) {
          userRoles.push('sentry_command_admin');
        } else {
          userIsNotRoles.push('sentry_command_admin');
        }

        const actions: Actions[] = [];
        userRoles.map(role =>
          actions.push({
            action: 'create',
            value: {
              user_type: 'user',
              user_id: user.auth0UserId,
              role_ids: [role],
            },
          }),
        );
        userIsNotRoles.map(role =>
          actions.push({
            action: 'delete',
            path: `/${user.auth0UserId}?role_ids=${role}`,
          }),
        );

        return actions;
      })
      .flat();

    const formattedUsersSplit = chunkArray(formattedUsers, 256);
    const errors: Errors[] = [];

    const promises = formattedUsersSplit.map(async chunk => {
      return baseFetch(
        'auth',
        `/permissions/orgs/${orgId}/user-permissions/bulk_update`,
        PermissionsUpdateResponseSchema,
        {
          method: 'POST',
          requestBody: chunk,
          searchParams: {
            ignore_duplicate_permission: String(true),
          },
        },
      ).then(res => {
        const chunkErrors = res.actions
          .map(action => action.errors)
          .flat()
          .filter(error => error.code >= 500);

        errors.push(...chunkErrors);
      });
    });

    await Promise.allSettled(promises);

    errors.forEach(error => {
      toast.error(error.message);
    });
    if (!errors?.length) {
      toast.success('Sentry permissions successfully updated.');
    }
  };

  return useMutation(['updateUserPermissions'], updateUserPermissions, {
    onSuccess: () => invalidatePermissions(),
  });
};
